/**
 * NotificationSystem Component
 * 
 * Two separate notification icons:
 * 1. Bell icon - System notifications (repair status changes, approvals needed)
 * 2. Chat icon - Chat messages only
 * 
 * Clicking a notification marks it as read and navigates to the relevant page
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Bell, MessageSquare, X } from 'lucide-react';
import api from '../lib/api';

export default function NotificationSystem() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  
  // System notifications (bell)
  const [showSystemDropdown, setShowSystemDropdown] = useState(false);
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [systemCount, setSystemCount] = useState(0);
  const systemRef = useRef(null);
  
  // Chat notifications (message icon)
  const [showChatDropdown, setShowChatDropdown] = useState(false);
  const [chatNotifications, setChatNotifications] = useState([]);
  const [chatCount, setChatCount] = useState(0);
  const chatRef = useRef(null);
  
  const [loading, setLoading] = useState(false);

  // Fetch all notifications and separate them
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/notifications');
      const all = response.notifications || [];
      
      // Separate by type
      const system = all.filter(n => n.type !== 'message');
      const chat = all.filter(n => n.type === 'message');
      
      setSystemNotifications(system);
      setChatNotifications(chat);
      setSystemCount(system.length);
      setChatCount(chat.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setSystemNotifications([]);
      setChatNotifications([]);
      setSystemCount(0);
      setChatCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts only (lighter for polling)
  const fetchCounts = async () => {
    try {
      // Get full list to separate counts (count endpoint doesn't separate types)
      const response = await api.get('/api/notifications');
      const all = response.notifications || [];
      
      // Separate counts
      const sysCount = all.filter(n => n.type !== 'message').length;
      const msgCount = all.filter(n => n.type === 'message').length;
      
      setSystemCount(sysCount);
      setChatCount(msgCount);
    } catch (error) {
      console.error('Error fetching notification counts:', error);
    }
  };

  // Load when dropdowns open
  useEffect(() => {
    if (showSystemDropdown || showChatDropdown) {
      fetchNotifications();
    }
  }, [showSystemDropdown, showChatDropdown]);

  // Poll for counts every 30 seconds
  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (systemRef.current && !systemRef.current.contains(event.target)) {
        setShowSystemDropdown(false);
      }
      if (chatRef.current && !chatRef.current.contains(event.target)) {
        setShowChatDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle notification click - mark as read and navigate
  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      if (notification.type === 'message') {
        await api.post(`/api/notifications/${notification.id}/read`);
      } else if (notification.type === 'inspection') {
        await api.post(`/api/notifications/inspection/${notification.id}/read`);
      }
      
      // Refresh
      await fetchNotifications();
      fetchCounts();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }

    // Close dropdowns
    setShowSystemDropdown(false);
    setShowChatDropdown(false);
    
    // Navigate based on type
    if (notification.type === 'message') {
      if (notification.subtype === 'repair' && notification.metadata?.repairId) {
        navigate('/repairs');
      } else if (notification.subtype === 'asset' && notification.metadata?.assetId) {
        navigate('/assets');
      }
    } else if (notification.type === 'inspection') {
      if (notification.metadata?.assetId) {
        navigate('/assets');
      }
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Render a notification item
  const NotificationItem = ({ notification }) => (
    <div
      onClick={() => handleNotificationClick(notification)}
      className={`px-4 py-3 cursor-pointer transition-colors border-b last:border-b-0 ${
        isDark
          ? 'hover:bg-gray-700 border-gray-700'
          : 'hover:bg-gray-50 border-gray-100'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isDark ? 'bg-blue-900/30' : 'bg-blue-100'
        }`}>
          {notification.type === 'inspection' ? '✓' : '🔔'}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {notification.title}
          </p>
          <p className={`text-xs mt-1 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {notification.message}
          </p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {formatTime(notification.timestamp)}
          </p>
        </div>
      </div>
    </div>
  );

  // Render a dropdown
  const Dropdown = ({ notifications, type }) => (
    <div
      className={`absolute right-0 mt-2 w-96 rounded-xl border shadow-2xl z-50 max-h-[500px] overflow-hidden flex flex-col ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      {/* Header */}
      <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {type === 'system' ? 'Notifications' : 'Messages'}
          </h3>
          <button
            onClick={() => type === 'system' ? setShowSystemDropdown(false) : setShowChatDropdown(false)}
            className={`p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <X size={16} className={isDark ? 'text-gray-400' : 'text-gray-600'} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="overflow-y-auto flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className={`text-4xl mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>
              {type === 'system' ? '🔔' : '💬'}
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              {type === 'system' ? 'No notifications' : 'No messages'}
            </p>
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem key={notification.id} notification={notification} />
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      {/* System Notifications (Bell) */}
      <div className="relative" ref={systemRef}>
        <button
          onClick={() => setShowSystemDropdown(!showSystemDropdown)}
          className={`relative p-2 rounded-lg transition-all ${
            isDark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
          }`}
          title="Notifications"
        >
          <Bell size={20} />
          {systemCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold px-1">
              {systemCount > 99 ? '99+' : systemCount}
            </span>
          )}
        </button>
        {showSystemDropdown && <Dropdown notifications={systemNotifications} type="system" />}
      </div>

      {/* Chat Notifications (Message) */}
      <div className="relative" ref={chatRef}>
        <button
          onClick={() => setShowChatDropdown(!showChatDropdown)}
          className={`relative p-2 rounded-lg transition-all ${
            isDark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
          }`}
          title="Messages"
        >
          <MessageSquare size={20} />
          {chatCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-blue-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold px-1">
              {chatCount > 99 ? '99+' : chatCount}
            </span>
          )}
        </button>
        {showChatDropdown && <Dropdown notifications={chatNotifications} type="chat" />}
      </div>
    </div>
  );
}

